define('services/validators',[], function () {
    "use strict";

    var roadUseValues = ["LimitedAccess", "Arterial", "Terminal", "Ramp", "Rotary", "LocalStreet"],
        routingGuidanceLanguageValues = ["ar", "af-ZA", "bg-BG", "zh-TW", "cs-CZ", "da-DK", "nl-NL", "en-GB", "en-US", "fi-FI", "fr-FR", "de-DE", "el-GR", "hu-HU", "id-ID", "it-IT", "ko-KR", "lt-LT", "ms-MY", "nb-NO", "pl-PL", "pt-BR", "pt-PT", "ru-RU", "sk-SK", "sl-SI", "es-ES", "es-MX", "sv-SE", "th-TH", "tr-TR"],
        trafficIncidentsLanguageValues = ["ca", "cs", "da", "de", "en", "es", "fi", "fr", "hu", "it", "nl", "no", "pl", "pt", "sk", "sv", "tr"];

    function isDateRfc3339(dateString) {
        return dateString.toString().match(/(\d\d\d\d)(-)?(\d\d)(-)?(\d\d)(T)?(\d\d)(:)?(\d\d)(:)?(\d\d)(\.\d+)?(Z|([+\-])(\d\d)(:)?(\d\d))/);
    }

    function validateAgainstSetOfValues(value, arrayOfValues, messageIfNotFound) {
        if (arrayOfValues.indexOf(value) === -1) {
            throw new TypeError(messageIfNotFound);
        }
        return value;
    }

    function hasBoundingBoxFields(bbox) {
        return bbox.hasOwnProperty("minLon") && bbox.hasOwnProperty("maxLon") && bbox.hasOwnProperty("minLat") && bbox.hasOwnProperty("maxLat");
    }

    function checkBBoxNormalized(bbox) {
        return bbox.maxLat <= bbox.minLat || bbox.maxLon <= bbox.minLon;
    }

    function checkInteger(value) {
        return !isFinite(value) || value % 1 !== 0;
    }

    function checkIntegerInInterval(value, min, max) {
        return checkInteger(value) || value <= min || value > max;
    }

    function checkFloatInInterval(numValue, min, max) {
        return !isFinite(numValue) || numValue < min || numValue > max;
    }

    function checkArray(array) {
        return array.constructor.toString().indexOf("Array") < 0;
    }

    return {

        /**
         * Casts to boolean
         * @param {*} value
         * @returns {Boolean} casted value
         */
        bool: function (value) {
            return value === "false" ? false : !!value;
        },

        /**
         * Check whether the given parameter is a float number
         * @param {*} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        number: function (value) {
            var numValue = parseFloat(value);
            if (!isFinite(numValue)) {
                throw new TypeError("a number is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a integer greater than or equal 0
         * @param {*} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        naturalInteger: function (value) {
            var numValue = parseFloat(value);
            if (checkInteger(numValue) || numValue < 0) {
                throw new TypeError("a natural integer (greater than or equal 0) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a integer greater than 0
         * @param {*} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        positiveInteger: function (value) {
            var numValue = parseFloat(value);
            if (checkInteger(numValue) || numValue <= 0) {
                throw new TypeError("a positive integer (greater than 0) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        integer: function (value) {
            var numValue = parseFloat(value);
            if (checkInteger(numValue)) {
                throw new TypeError("an integer is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a longitude and casts to proper interval <-180, 180>
         * @param {*} value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        longitude: function (value) {
            var numValue = parseFloat(value);
            if (checkFloatInInterval(numValue, -180, 180)) {
                throw new TypeError("an longitude <-180,180> is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a latitude and casts to proper interval <-90, 90>
         * @param {*} value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        latitude: function (value) {
            var numValue = parseFloat(value);
            if (checkFloatInInterval(numValue, -90, 90)) {
                throw new TypeError("an latitude <-90,90> is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a valid bounding box. Allows to contain whole world and a half on each
         * side.
         * @param {*} bbox The bounding box
         * @returns {Object} bbox
         * @throws {TypeError} for invalid parameter
         */
        boundingBox: function (bbox) {
            if (!hasBoundingBoxFields(bbox)) {
                throw new TypeError("a bounding box is expected, but " + bbox + " [" + (typeof bbox) + "] given");
            }
            if (checkFloatInInterval(bbox.minLat, -90, 90)) {
                throw new TypeError("a bounding box minimal latitude is expected " + bbox.minLat + " [" + (typeof bbox.minLat) + "] given");
            }
            if (checkFloatInInterval(bbox.maxLat, -90, 90)) {
                throw new TypeError("a bounding box maximal latitude is expected " + bbox.maxLat + " [" + (typeof bbox.maxLat) + "] given");
            }
            if (checkFloatInInterval(bbox.minLon, -270, 180)) {
                throw new TypeError("a bounding box minimal longitude is expected " + bbox.minLon + " [" + (typeof bbox.minLon) + "] given");
            }
            if (checkFloatInInterval(bbox.maxLon, -180, 270)) {
                throw new TypeError("a bounding box maximal longitude is expected " + bbox.maxLon + " [" + (typeof bbox.maxLon) + "] given");
            }
            if (checkBBoxNormalized(bbox)) {
                throw new TypeError("a bounding box expected but max <= min");
            }
            return bbox;
        },

        /**
         * Check whether the given parameter is a float number in proper interval <min, max>
         * @example
         * var numberBetweenMinusFiveAndFive = validators.numberInInterval(-5,5);
         * numberBetweenMinusFiveAndFive(-10); // false
         * numberBetweenMinusFiveAndFive(3); //true
         *
         * @param {Number} [min] The minimal value
         * @param {Number} [max] The maximal value
         * @returns {Function} validator function that checks type and value range
         * @throws {TypeError} when missing min or max value
         */
        numberInInterval: function (min, max) {
            if (arguments.length !== 2) {
                throw new TypeError("Number in interval validator requires min and max value parameters");
            }
            return function (value) {
                var numValue = parseFloat(value);
                if (checkFloatInInterval(numValue, min, max)) {
                    throw new TypeError("a number in interval <" + min + ", " + max + "> is expected, but " + value + " [" + (typeof value) + "] given");
                }
                return numValue;
            };
        },

        /**
         * Check whether the given parameter is an integer number in proper interval <min, max>
         * @example
         * var numberBetweenMinusFiveAndFive = validators.numberInInterval(-5,5);
         * numberBetweenMinusFiveAndFive(-10); // false
         * numberBetweenMinusFiveAndFive(3); //true
         *
         * @param {Number} [min] The minimal value
         * @param {Number} [max] The maximal value
         * @returns {Function} validator function that checks type and value range
         * @throws {TypeError} when missing min or max value
         */
        integerInInterval: function (min, max) {
            if (arguments.length !== 2) {
                throw new TypeError("Number in interval validator requires min and max value parameters");
            }
            return function (value) {
                var numValue = parseFloat(value);
                if (checkIntegerInInterval(numValue, min, max)) {
                    throw new TypeError("an integer in interval <" + (min + 1) + ", " + max + "> is expected, but " + value + " [" + (typeof value) + "] given");
                }
                return numValue;
            };
        },

        /**
         * Check whether the given parameter is a string
         * @param {String} string
         * @returns {String} value
         * @throws {TypeError} for non strings
         */
        string: function (string) {
            if (typeof string !== "string") {
                throw new TypeError("a string is expected, but " + string + " [" + (typeof string) + "] given");
            }
            return string;
        },

        /**
         * Check whether the given parameter is a zoom level (0, 18>
         * @param {Number} zoomLevel
         * @returns {Number} value
         * @throws {TypeError} for non invalid parameter
         */
        zoomLevel: function (zoomLevel) {
            var numZoomLevel = parseFloat(zoomLevel);
            if (checkIntegerInInterval(numZoomLevel, 0, 18)) {
                throw new TypeError("an zoom level (0, 18> is expected, but " + zoomLevel + " [" + (typeof zoomLevel) + "] given");
            }
            return numZoomLevel;
        },

        /**
         * Check whether the given parameter is a function
         * @param {Function} fun
         * @returns {Function} fun
         * @throws {TypeError} for non function
         */
        functionType: function (fun) {
            if (typeof fun !== "function") {
                throw new TypeError("a function is expected, but " + fun + "  [" + (typeof fun) + "] given");
            }
            return fun;
        },

        /**
         * Checks whether the given country name uses ISO 3166 alpha-3 or alpha-2 format.
         * @param {String} name
         * @returns {String} name
         * @throws {TypeError} if the country name is not valid
         */
        countryCode: function (name) {
            if (!(typeof name === "string" && (name.length === 3 || name.length === 2))) {
                throw new TypeError("a 2 or 3-characters long country name is expected, but " + name + " [" + (typeof name) + "] given");
            }
            return name;
        },

        /**
         * Checks whether the given country name's list use ISO 3166 alpha-3 or alpha-2 format.
         * @param {String|Array} value
         * @returns {String} list
         * @throws {TypeError} if one of the country names is not valid
         */
        countrySet: function (value) {
            if (typeof value !== "string" && checkArray(value)) {
                throw new TypeError("An array of string country names or string (divided with commas) of country names (two or three-characters long) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            var stringValue = (typeof value === "string") ? value : value.join();
            if (!stringValue.match(/^([a-zA-z]{2,3},)*[a-zA-z]{2,3}$/)) {
                throw new TypeError("An array of string country names or string (divided with commas) of country names (two or three-characters long) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return stringValue;
        },

        /**
         * Checks whether the given point is in proper format
         * @param {String} value
         * @returns {String} value
         * @throws {TypeError} if the given value is in not valid format
         */
        point: function (value) {
            if (typeof value !== "string" || !value.match(/^(-?\d*(?:\.\d*)?)(?:\s+|\s*,\s*)(-?\d*(?:\.\d*)?)$/)) {
                throw new TypeError("A point is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return value;
        },

        /**
         * Check whether the given parameter is a positive integer (greater than 0) and lower than 3
         * @param {Number} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        fuzzinessLevel: function (value) {
            var numValue = parseFloat(value);
            if (checkIntegerInInterval(numValue, 0, 3)) {
                throw new TypeError("Fuzziness level value (a positive integer lower than 3) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a positive integer (greater than 0) and lower than 100
         * @param {Number} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        limit: function (value) {
            var numValue = parseFloat(value);
            if (checkIntegerInInterval(numValue, 0, 100)) {
                throw new TypeError("Limit value (a positive integer lower than 100) is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is an offset - positive integer <0, 1900>
         * @param {Number} value The value
         * @returns {Number} value
         * @throws {TypeError} for invalid parameter
         */
        offset: function (value) {
            var numValue = parseFloat(value);
            if (checkIntegerInInterval(numValue, -1, 1900)) {
                throw new TypeError("Offset an integer value <0, 1900> is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return numValue;
        },

        /**
         * Check whether the given parameter is a plain object (array and functions aren't accepted)
         * @param {Object} object
         * @returns {Object} object
         * @throws {TypeError} for non object
         */
        plainObject: function (object) {
            if (typeof object !== "object" || object.constructor !== Object) {
                throw new TypeError("an object is expected, but " + object + "  [" + (typeof object) + "] given");
            }
            return object;
        },

        /**
         * Check whether the given parameter is an array (objects and functions aren't accepted)
         * @param {Array} array
         * @returns {Array} array
         * @throws {TypeError} for non array
         */
        arrayType: function (array) {
            if (checkArray(array)) {
                throw new TypeError("an array is expected, but " + array + "  [" + (typeof array) + "] given");
            }
            return array;
        },

        roadUse: function (value) {
            if (typeof value !== "string" || !value.match(/^\[("\w*",?)+\]$/)) {
                throw new TypeError("Road use is expected, but " + value + "  [" + (typeof value) + "] given");
            }
            var elements = value.replace(/["\[\]]/g, "").split(",");
            for (var i = 0; i < elements.length; i += 1) {
                if (roadUseValues.indexOf(elements[i]) < 0) {
                    throw new TypeError("Road use (" + roadUseValues + ") is expected, but " + value + "  [" + (typeof value) + "] given");
                }
            }
            return value;
        },

        oneOfValue: function (values, paramName) {
            return function (value) {
                var message = "Supported " + paramName + " is expected (one of: " + values + "), but " + value + " [" + (typeof value) + "] given";
                return validateAgainstSetOfValues(value, values, message);
            };
        },

        arrayOfValues: function (values, paramName) {
            return function (value) {
                var message = "Supported " + paramName + " type is expected (array with one of: " + values + "), but " + value + " [" + (typeof value) + "] given";
                if (checkArray(value)) {
                    throw new TypeError("an array is expected, but " + value + "  [" + (typeof value) + "] given");
                }
                for (var i = 0; i < value.length; i += 1) {
                    validateAgainstSetOfValues(value[i], values, message);
                }
                return value;
            };
        },

        /**
         * Checks whether the given departure time is now or in proper rfc3339 format ( 1996-12-19T16:39:57 or
         * 1996-12-19T16:39:57-08:00 ):
         * @param {String} value
         * @returns {String} value
         * @throws {TypeError} if the given value is not valid
         */
        departAt: function (value) {
            if (value === "now" || (isDateRfc3339(value) && Date.now() < Date.parse(value))) {
                return value;
            } else {
                throw new TypeError("Supported departAt is now or rfc3339 format and no earlier than now(), but " + value + " [" + (typeof value) + "] given");
            }
        },

        /**
         * Checks whether the given arrive time is in proper rfc3339 format ( 1996-12-19T16:39:57 or
         * 1996-12-19T16:39:57-08:00 ):
         * @param {String} value
         * @returns {String} value
         * @throws {TypeError} if the given value is not valid
         */
        arriveAt: function (value) {
            if (isDateRfc3339(value) && Date.now() < Date.parse(value)) {
                return value;
            } else {
                throw new TypeError("Supported arriveAt is rfc3339 format, but and no earlier than now() " + value + " [" + (typeof value) + "] given");
            }
        },

        routingGuidanceLanguage: function (value) {
            var message = "Supported routing guidance language is expected to be one of: " + routingGuidanceLanguageValues + "), but " + value + " [" + (typeof value) + "] given";
            return validateAgainstSetOfValues(value, routingGuidanceLanguageValues, message);
        },

        trafficIncidentsLanguage: function (value) {
            var message = "Supported traffic incidents language is expected to be one of: " + trafficIncidentsLanguageValues + "), but " + value + " [" + (typeof value) + "] given";
            return validateAgainstSetOfValues(value, trafficIncidentsLanguageValues, message);
        },

        /**
         * Checks whether the given routing locations is in proper format:
         * circle A circle with a center point and a radius (in meters).
         * The radius must be a positive integer with the maximum value of 20050000. Note that larger integer values
         * will not be rejected but lowered to the maximum value. circle(52.37245,4.89406,10000) location    A point or
         * a circle.
         * 52.37245,4.89406
         * circle(52.37245,4.89406,10000)
         * @param {String} value
         * @returns {String} value
         * @throws {TypeError} if the given value is not valid
         */
        routingLocations: function (value) {
            if (value.constructor.toString().indexOf("Array") > -1) {
                value = value.join(":");
            }
            var point = /(-?\d*(?:\.\d*)?)(?:\s+|\s*,\s*)(-?\d*(?:\.\d*)?)/;
            var circle = /circle\(-?\d*(?:\.\d*)?\s*,\s*-?\d*(?:\.\d*)?\s*,\s*(\d+)\)/;
            var routingLocation = new RegExp("^" + point.source + ":(?:(?:" + point.source + "|" + circle.source + "):)*" + point.source + "$");
            if (!value.match(routingLocation)) {
                throw new TypeError("Routing location is expected. But " + value + " [" + (typeof value) + "] given");
            }
            return value;
        },

        /**
         * Checks whether the given input is in proper circle format:
         * circle A circle with a center point and a radius (in meters).
         * The radius must be a positive integer with the maximum value of 20050000. Note that larger integer values
         * will not be rejected but lowered to the maximum value. circle(52.37245,4.89406,10000)
         * @param {String} value
         * @returns {String} value
         * @throws {TypeError} if the given value is not valid
         */
        circle: function (value) {
            if (typeof value !== "string") {
                throw new TypeError("Expecting circle but " + value + " [" + (typeof value) + "] given");
            }
            var match = value.match(/circle\(-?\d*(?:\.\d*)?\s*,\s*-?\d*(?:\.\d*)?\s*,\s*(\d+)\)/);
            if (!match || parseFloat(match[1]) > 20050000) {
                throw new TypeError("Expecting circle but " + value + " [" + (typeof value) + "] given");
            }
            return value;
        },

        /**
         * Checks whether the given input is in proper geometry list format.
         * Geometry list represents list of figures (polygons or circles) objects.
         * Circle contains properties like: type ("CIRCLE"), position (lat & lon) and radius.
         * Polygon contains properties like: type ("POLYGON") and vertices (as a list of points).
         *
         * @param {Array} value
         * @returns {String} value Stringified array of figures (JSON format).
         * @throws {TypeError} if the given value is not valid
         */
        geometryList: function (value) {
            if (checkArray(value)) {
                throw new TypeError("An array of geometry objects is expected, but " + value + " [" + (typeof value) + "] given");
            }
            if (value.length > 0) {
                for (var i = 0; i < value.length; i += 1) {
                    var geoObject = value[i];
                    if (!geoObject.hasOwnProperty("type") || (!geoObject.hasOwnProperty("vertices") && (!geoObject.hasOwnProperty("position") || !geoObject.hasOwnProperty("radius")))) {
                        throw new TypeError("An array of geometry objects is expected, but " + value + " [" + (typeof value) + "] given");
                    }
                }
            } else {
                throw new TypeError("An array of geometry objects is expected, but " + value + " [" + (typeof value) + "] given");
            }
            return JSON.stringify(value);
        }
    };
});

